import { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { useBrand } from "@/config/brands";
import { useTheme } from "@/contexts/ThemeContext";
import { Helmet } from "react-helmet-async";
import {
  Info,
  FileText,
  Shield,
  Mail,
  Github,
  Heart,
  Code,
  Database,
  ExternalLink,
  ChevronRight,
  RefreshCw,
  Trash2,
  Cpu
} from "lucide-react";
import { Link } from "react-router-dom";
import appIcon from "@/assets/app-icon.png";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { useAdminStatus } from "@/hooks/useAdminStatus";
import { useAppReviewFlags } from "@/config/appReviewFlags";
import { isNativeApp } from "@/lib/platform";
import { AhpNative } from "@/lib/nativeBridge";
import { useTranslation } from 'react-i18next';

const Settings = () => {
  const brand = useBrand();
  const { isDark } = useTheme();
  const { t } = useTranslation(['settings', 'common']);
  const { data: adminStatus } = useAdminStatus();
  const isAdmin = adminStatus?.isAdmin;
  // App-review gates: donation/payment surfaces hidden on native until the
  // remote flag flips; ads (and the CCPA ad-preferences row) never show on native.
  const { showPayments } = useAppReviewFlags();
  const native = isNativeApp();
  // Pre-1.0 on purpose: the platform is launched but still filling in coverage,
  // and claiming 1.0.0 oversells where it actually is.
  // The installed app reports its real App Store version/build instead.
  const [appVersion, setAppVersion] = useState("0.9.2");
  const [buildNumber, setBuildNumber] = useState("2025.01");
  useEffect(() => {
    if (!native) return;
    AhpNative.appInfo()
      .then(({ version, build }) => { setAppVersion(version); setBuildNumber(build); })
      .catch(() => {});
  }, [native]);
  const { toast } = useToast();

  const isAhp = brand.key === 'ahp';
  const isAntigreed = brand.key === 'antigreed';
  const ahpDark = isAhp && isDark;

  // AHP dark mode color tokens (following ForBusiness.tsx pattern)
  const pageBg = ahpDark ? '#0a1628' : isAhp ? '#FAF9F6' : undefined;
  const cardBg = ahpDark ? '#111f36' : isAhp ? '#FFFFFF' : undefined;
  const borderColor = ahpDark ? 'rgba(197,165,90,0.2)' : isAhp ? 'rgba(51,51,52,0.12)' : undefined;
  const ink = ahpDark ? '#f0ead6' : isAhp ? '#333334' : undefined;
  const subtle = ahpDark ? 'rgba(240,234,214,0.5)' : isAhp ? 'rgba(51,51,52,0.55)' : undefined;
  const forest = ahpDark ? '#5aad80' : '#2b5e49';
  const forestBg = ahpDark ? 'rgba(90,173,128,0.12)' : 'rgba(43,94,73,0.1)';
  const hoverBg = ahpDark ? 'rgba(197,165,90,0.08)' : undefined;
  const headerBorderColor = ahpDark ? 'rgba(197,165,90,0.15)' : isAhp ? 'rgba(51,51,52,0.12)' : undefined;
  const versionBg = ahpDark ? 'rgba(197,165,90,0.12)' : isAhp ? '#F7F4EF' : undefined;
  const versionText = ahpDark ? '#C5A55A' : isAhp ? '#2b5e49' : undefined;

  // Cost-display defaults for scenario cards — persisted to the same localStorage
  // keys the scenario page reads. stat = avg|median; scope = capped|raw.
  const [costStatPref, setCostStatPref] = useState<'avg' | 'median'>(() => {
    if (typeof window === 'undefined') return 'avg';
    return (window.localStorage.getItem('ahp-card-cost-stat') as 'avg' | 'median') || 'avg';
  });
  const [costScopePref, setCostScopePref] = useState<'capped' | 'raw'>(() => {
    if (typeof window === 'undefined') return 'capped';
    return (window.localStorage.getItem('ahp-card-cost-scope') as 'capped' | 'raw') || 'capped';
  });
  const applyCostStatPref = (v: 'avg' | 'median') => { setCostStatPref(v); try { window.localStorage.setItem('ahp-card-cost-stat', v); } catch {} };
  const applyCostScopePref = (v: 'capped' | 'raw') => { setCostScopePref(v); try { window.localStorage.setItem('ahp-card-cost-scope', v); } catch {} };

  // Check if running in WebViewGold wrapper
  const isWebViewGold = navigator.userAgent.includes('HoS_iOS');

  const queryClient = useQueryClient();

  const handleReload = () => {
    if (isWebViewGold) {
      // Use WebViewGold reset command to clear cache and reload
      window.location.href = 'reset://';
    } else {
      // Use standard web reload
      window.location.reload();
    }
  };

  const handleClearCache = async () => {
    // Clear React Query cache
    queryClient.clear();
    // Clear service worker caches
    if ('caches' in window) {
      const names = await caches.keys();
      await Promise.all(names.map(name => caches.delete(name)));
    }
    // Clear legacy localStorage cache
    localStorage.removeItem('hospital_rankings_cache');
    localStorage.removeItem('hospital_rankings_cache_timestamp');
    toast({
      title: t('settings:cache.clearedTitle'),
      description: t('settings:cache.clearedDescription'),
    });
  };

  const settingsSections = [
    {
      title: t('settings:sections.app'),
      items: [
        {
          icon: RefreshCw,
          label: t('settings:items.reloadApp.label'),
          description: t('settings:items.reloadApp.description'),
          action: handleReload
        }
      ]
    },
    {
      title: t('settings:sections.data'),
      items: [
        {
          icon: Trash2,
          label: t('settings:items.clearRankingsCache.label'),
          description: t('settings:items.clearRankingsCache.descriptionEmpty'),
          action: handleClearCache,
        }
      ]
    },
    {
      title: t('settings:sections.about'),
      items: [
        {
          icon: Info,
          label: t('settings:items.aboutThisApp.label'),
          description: t('settings:items.aboutThisApp.description'),
          action: () => window.scrollTo({ top: document.getElementById('about-section')?.offsetTop || 0, behavior: 'smooth' })
        },
        ...(showPayments ? [{
          icon: Heart,
          label: t('settings:items.supportUs.label'),
          description: t('settings:items.supportUs.description'),
          link: "/donate"
        }] : [])
      ]
    },
    {
      title: t('settings:sections.legal'),
      items: [
        {
          icon: FileText,
          label: t('settings:items.termsOfService.label'),
          description: t('settings:items.termsOfService.description'),
          link: "/terms"
        },
        {
          icon: Shield,
          label: t('settings:items.privacyPolicy.label'),
          description: t('settings:items.privacyPolicy.description'),
          link: "/privacy"
        },
        {
          icon: FileText,
          label: t('settings:items.dataSources.label'),
          description: t('settings:items.dataSources.description'),
          link: "/data-sources"
        },
        {
          icon: Database,
          label: t('settings:items.dataMethodology.label'),
          description: t('settings:items.dataMethodology.description'),
          link: "/data-methodology"
        },
        {
          icon: FileText,
          label: t('settings:items.lawsuits.label'),
          description: t('settings:items.lawsuits.description'),
          link: "/lawsuits"
        },
        // Native app shows no ads and uses OS privacy controls, so the CCPA
        // ad-preferences row is web-only.
        ...(!native ? [{
          icon: Shield,
          label: "Do Not Sell or Share My Personal Information",
          description: "Manage your advertising and data sharing preferences (CCPA/CPRA)",
          action: () => {
            // Open cookie preferences with advertising toggle
            const consent = localStorage.getItem("cookie-consent");
            const prefs = consent ? JSON.parse(consent) : { necessary: true, analytics: true, advertising: true };
            prefs.advertising = false;
            localStorage.setItem("cookie-consent", JSON.stringify(prefs));
            if (window.gtag) {
              window.gtag('consent', 'update', {
                'ad_storage': 'denied',
                'ad_user_data': 'denied',
                'ad_personalization': 'denied'
              });
            }
            alert("Ad personalization has been disabled. You can re-enable it anytime from this page.");
          }
        }] : [])
      ]
    },
    {
      title: t('settings:sections.support'),
      items: [
        {
          icon: Mail,
          label: t('settings:items.contactUs.label'),
          description: t('settings:items.contactUs.description'),
          action: () => window.location.href = `mailto:${brand.contact.support}`
        },
        {
          icon: Github,
          label: t('settings:items.reportIssue.label'),
          description: t('settings:items.reportIssue.description'),
          action: () => window.location.href = `mailto:${brand.contact.support}?subject=${encodeURIComponent(`Issue report — ${brand.name}`)}`
        }
      ]
    },
    {
      title: t('settings:sections.developer'),
      items: [
        {
          icon: Cpu,
          label: t('settings:items.mcpServer.label'),
          description: t('settings:items.mcpServer.description'),
          link: "/mcp"
        },
        {
          icon: Code,
          label: t('settings:items.apiAccess.label'),
          description: t('settings:items.apiAccess.description'),
          link: "/api"
        }
      ]
    }
  ];

  // Helper for card class
  const cardClass = isAntigreed
    ? 'bg-card border-border'
    : isAhp
    ? '' // Use inline styles for dark mode
    : 'border-matrix-green/20';

  // Helper for section heading color
  const sectionHeadingColor = isAntigreed ? 'text-gray-600' : isAhp ? undefined : 'text-muted-foreground';

  return (
    <div
      className={`min-h-screen pb-24 ${isAntigreed ? 'bg-background' : !isAhp ? 'bg-background' : ''}`}
      style={isAhp ? { backgroundColor: pageBg } : undefined}
    >
      <Helmet>
        <title>{t('settings:meta.pageTitle', { brandName: brand.name })}</title>
        <meta name="description" content={t('settings:meta.description', { brandName: brand.name })} />
        <meta name="robots" content="noindex, follow" />
      </Helmet>

      {/* Header */}
      <div
        className={isAntigreed
          ? 'bg-secondary/70 border-b border-border'
          : !isAhp
          ? 'bg-gradient-to-br from-matrix-green/10 to-blood-red/10 border-b border-matrix-green/20'
          : ''
        }
        style={isAhp ? { borderBottom: `1px solid ${headerBorderColor}` } : undefined}
      >
        <div className="max-w-2xl mx-auto px-6 py-8">
          <h1
            className={`text-3xl font-bold mb-2 ${isAntigreed ? 'text-emerald-800 font-serif' : !isAhp ? 'text-foreground' : ''}`}
            style={isAhp ? { color: ink, fontFamily: "'Instrument Sans', system-ui, sans-serif" } : undefined}
          >
            {t('settings:pageTitle')}
          </h1>
          <p
            className={isAntigreed ? 'text-gray-700 font-medium' : !isAhp ? 'text-muted-foreground' : ''}
            style={isAhp ? { color: subtle } : undefined}
          >
            {t('settings:pageSubtitle')}
          </p>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-6 py-6 space-y-6">
        {/* App Info Card */}
        <Card
          className={isAntigreed ? 'bg-card border-border' : !isAhp ? 'border-matrix-green/20' : ''}
          style={isAhp ? { backgroundColor: cardBg, borderColor: borderColor } : undefined}
        >
          <CardHeader className="text-center">
            <img
              src={isAntigreed ? '/antigreed-icon.png' : isAhp ? '/consumer/logo-square.png' : appIcon}
              alt={`${brand.name} App Icon`}
              className="mx-auto mb-4 object-cover shadow-lg"
              style={{ width: '125px', height: '125px', borderRadius: '20px' }}
            />
            <CardTitle
              className={`text-2xl ${isAntigreed ? 'text-emerald-800 font-serif' : !isAhp ? '' : ''}`}
              style={isAhp ? { color: ink } : undefined}
            >
              {brand.name}
            </CardTitle>
            <CardDescription
              className={`text-base ${isAntigreed ? 'text-gray-700' : !isAhp ? '' : ''}`}
              style={isAhp ? { color: subtle } : undefined}
            >
              {brand.messaging.heroSubtitle}
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center space-y-2">
            <div
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-mono ${
                isAntigreed ? 'bg-emerald-50 text-emerald-800' : !isAhp ? 'bg-secondary' : ''
              }`}
              style={isAhp ? { backgroundColor: versionBg, color: versionText } : undefined}
            >
              <Code className="w-4 h-4" />
              {t('settings:version.label')} {appVersion}
            </div>
            <div
              className={`text-xs ${isAntigreed ? 'text-gray-600' : !isAhp ? 'text-muted-foreground' : ''}`}
              style={isAhp ? { color: subtle } : undefined}
            >
              {t('settings:version.build')} {buildNumber}
            </div>
          </CardContent>
        </Card>

        {/* Cost Display preferences (AHP only) */}
        {isAhp && (
          <div className="space-y-3">
            <h2 className="text-sm font-semibold uppercase tracking-wide px-2" style={{ color: subtle }}>
              {t('settings:costDisplay.title', { defaultValue: 'Scenario Cost Display' })}
            </h2>
            <Card className={cardClass} style={{ backgroundColor: cardBg, borderColor: borderColor }}>
              <CardContent className="p-4 space-y-5">
                {/* Default statistic */}
                <div>
                  <div className="font-medium mb-1" style={{ color: ink }}>
                    {t('settings:costDisplay.statLabel', { defaultValue: 'Default cost figure' })}
                  </div>
                  <div className="text-sm mb-3" style={{ color: subtle }}>
                    {t('settings:costDisplay.statDesc', { defaultValue: 'Average (mean of hospital prices) or median (middle value, resists extremes).' })}
                  </div>
                  <div className="inline-flex rounded-lg p-0.5" style={{ background: ahpDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,40,104,0.06)', border: `1px solid ${borderColor}` }}>
                    {(['avg', 'median'] as const).map((v) => (
                      <button key={v} type="button" onClick={() => applyCostStatPref(v)}
                        className="text-xs font-semibold uppercase tracking-wider px-4 py-1.5 rounded-md transition-colors"
                        style={{
                          background: costStatPref === v ? (ahpDark ? '#C5A55A' : '#002868') : 'transparent',
                          color: costStatPref === v ? '#fff' : (subtle as string),
                        }}>
                        {v === 'avg' ? t('settings:costDisplay.avg', { defaultValue: 'Average' }) : t('settings:costDisplay.median', { defaultValue: 'Median' })}
                      </button>
                    ))}
                  </div>
                </div>
                {/* Outlier capping */}
                <div>
                  <div className="font-medium mb-1" style={{ color: ink }}>
                    {t('settings:costDisplay.scopeLabel', { defaultValue: 'Outlier handling (averages)' })}
                  </div>
                  <div className="text-sm mb-3" style={{ color: subtle }}>
                    {t('settings:costDisplay.scopeDesc', { defaultValue: 'Capped winsorizes extreme hospital charges at 25× the median (recommended). Raw shows the true uncapped average.' })}
                  </div>
                  <div className="inline-flex rounded-lg p-0.5" style={{ background: ahpDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,40,104,0.06)', border: `1px solid ${borderColor}` }}>
                    {(['capped', 'raw'] as const).map((v) => (
                      <button key={v} type="button" onClick={() => applyCostScopePref(v)}
                        className="text-xs font-semibold uppercase tracking-wider px-4 py-1.5 rounded-md transition-colors"
                        style={{
                          background: costScopePref === v ? (ahpDark ? '#5a7da8' : '#002868') : 'transparent',
                          color: costScopePref === v ? '#fff' : (subtle as string),
                        }}>
                        {v === 'capped' ? t('settings:costDisplay.capped', { defaultValue: 'Capped' }) : t('settings:costDisplay.raw', { defaultValue: 'Raw' })}
                      </button>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Settings Sections */}
        {settingsSections.map((section, sectionIndex) => (
          <div key={sectionIndex} className="space-y-3">
            <h2
              className={`text-sm font-semibold uppercase tracking-wide px-2 ${sectionHeadingColor || ''}`}
              style={isAhp ? { color: subtle } : undefined}
            >
              {section.title}
            </h2>
            <Card
              className={cardClass}
              style={isAhp ? { backgroundColor: cardBg, borderColor: borderColor } : undefined}
            >
              <CardContent className="p-0">
                {section.items.map((item, itemIndex) => {
                  const IconComponent = item.icon;
                  const content = (
                    <>
                      <div className="flex items-center gap-4 flex-1">
                        <div
                          className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                            isAntigreed ? 'bg-emerald-100' : !isAhp ? 'bg-matrix-green/10' : ''
                          }`}
                          style={isAhp ? { backgroundColor: forestBg } : undefined}
                        >
                          <IconComponent
                            className={`w-5 h-5 ${isAntigreed ? 'text-emerald-700' : !isAhp ? 'text-matrix-green' : ''}`}
                            style={isAhp ? { color: forest } : undefined}
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div
                            className={`font-medium ${isAntigreed ? 'text-gray-900' : !isAhp ? 'text-foreground' : ''}`}
                            style={isAhp ? { color: ink } : undefined}
                          >
                            {item.label}
                          </div>
                          <div
                            className={`text-sm ${isAntigreed ? 'text-gray-600' : !isAhp ? 'text-muted-foreground' : ''}`}
                            style={isAhp ? { color: subtle } : undefined}
                          >
                            {item.description}
                          </div>
                        </div>
                      </div>
                      <ChevronRight
                        className={`w-5 h-5 flex-shrink-0 ${isAntigreed ? 'text-gray-400' : !isAhp ? 'text-muted-foreground' : ''}`}
                        style={isAhp ? { color: subtle } : undefined}
                      />
                    </>
                  );

                  return (
                    <div key={itemIndex}>
                      {item.link ? (
                        <Link
                          to={item.link}
                          className={`flex items-center gap-3 p-4 transition-colors ${
                            isAntigreed ? 'hover:bg-accent/50' : !isAhp ? 'hover:bg-accent/50' : ''
                          }`}
                          style={isAhp ? { ['--hover-bg' as any]: hoverBg } : undefined}
                          onMouseEnter={isAhp ? (e) => (e.currentTarget.style.backgroundColor = ahpDark ? 'rgba(197,165,90,0.08)' : 'rgba(43,94,73,0.05)') : undefined}
                          onMouseLeave={isAhp ? (e) => (e.currentTarget.style.backgroundColor = 'transparent') : undefined}
                        >
                          {content}
                        </Link>
                      ) : (
                        <button
                          onClick={item.action}
                          disabled={(item as any).disabled}
                          className={`w-full flex items-center gap-3 p-4 transition-colors text-left ${
                            (item as any).disabled
                              ? 'opacity-50 cursor-not-allowed'
                              : isAntigreed ? 'hover:bg-accent/50' : !isAhp ? 'hover:bg-accent/50' : ''
                          }`}
                          onMouseEnter={isAhp && !(item as any).disabled ? (e) => (e.currentTarget.style.backgroundColor = ahpDark ? 'rgba(197,165,90,0.08)' : 'rgba(43,94,73,0.05)') : undefined}
                          onMouseLeave={isAhp && !(item as any).disabled ? (e) => (e.currentTarget.style.backgroundColor = 'transparent') : undefined}
                        >
                          {content}
                        </button>
                      )}
                      {itemIndex < section.items.length - 1 && (
                        <Separator
                          className="mx-4"
                          style={isAhp ? { backgroundColor: borderColor } : undefined}
                        />
                      )}
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          </div>
        ))}

        {/* Admin Section - only for admins */}
        {isAdmin && (
          <div className="space-y-3">
            <h2
              className={`text-sm font-semibold uppercase tracking-wide px-2 ${sectionHeadingColor || ''}`}
              style={isAhp ? { color: subtle } : undefined}
            >
              Admin
            </h2>
            <Card
              className={cardClass}
              style={isAhp ? { backgroundColor: cardBg, borderColor: borderColor } : undefined}
            >
              <CardContent className="p-0">
                <Link
                  to="/admin"
                  className={`flex items-center gap-3 p-4 transition-colors ${
                    isAntigreed ? 'hover:bg-accent/50' : !isAhp ? 'hover:bg-accent/50' : ''
                  }`}
                  onMouseEnter={isAhp ? (e) => (e.currentTarget.style.backgroundColor = ahpDark ? 'rgba(197,165,90,0.08)' : 'rgba(43,94,73,0.05)') : undefined}
                  onMouseLeave={isAhp ? (e) => (e.currentTarget.style.backgroundColor = 'transparent') : undefined}
                >
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                      isAntigreed ? 'bg-emerald-100' : !isAhp ? 'bg-matrix-green/10' : ''
                    }`}
                    style={isAhp ? { backgroundColor: forestBg } : undefined}
                  >
                    <Shield
                      className={`w-5 h-5 ${isAntigreed ? 'text-emerald-700' : !isAhp ? 'text-matrix-green' : ''}`}
                      style={isAhp ? { color: forest } : undefined}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div
                      className={`font-medium ${isAntigreed ? 'text-gray-900' : !isAhp ? 'text-foreground' : ''}`}
                      style={isAhp ? { color: ink } : undefined}
                    >
                      Admin Panel
                    </div>
                    <div
                      className={`text-sm ${isAntigreed ? 'text-gray-600' : !isAhp ? 'text-muted-foreground' : ''}`}
                      style={isAhp ? { color: subtle } : undefined}
                    >
                      System administration and tools
                    </div>
                  </div>
                  <ChevronRight
                    className={`w-5 h-5 flex-shrink-0 ${isAntigreed ? 'text-gray-400' : !isAhp ? 'text-muted-foreground' : ''}`}
                    style={isAhp ? { color: subtle } : undefined}
                  />
                </Link>
              </CardContent>
            </Card>
          </div>
        )}

        {/* About Section */}
        <Card
          id="about-section"
          className={isAntigreed ? 'bg-card border-border' : !isAhp ? 'border-matrix-green/20' : ''}
          style={isAhp ? { backgroundColor: cardBg, borderColor: borderColor } : undefined}
        >
          <CardHeader>
            <CardTitle
              className={isAntigreed ? 'text-emerald-800 font-serif' : ''}
              style={isAhp ? { color: ink } : undefined}
            >
              {t('settings:mission.title')}
            </CardTitle>
          </CardHeader>
          <CardContent
            className={`space-y-4 text-sm leading-relaxed ${
              isAntigreed ? 'text-gray-700' : !isAhp ? 'text-muted-foreground' : ''
            }`}
            style={isAhp ? { color: subtle } : undefined}
          >
            {isAhp && (
              <p style={{ color: ink, fontWeight: 600 }}>
                Prices, not medical advice. Every price comes from a hospital&apos;s own published price file; talk to your
                doctor about your care, and to the hospital and your insurer about what you&apos;ll actually pay.
              </p>
            )}
            {isAntigreed ? (
              <>
                <p>{t('settings:mission.antigreedDescription1', { brandName: brand.name })}</p>
                <p>{t('settings:mission.antigreedDescription2')}</p>
                <p>{t('settings:mission.antigreedDescription3')}</p>
              </>
            ) : isAhp ? (
              <>
                <p>{t('settings:mission.ahpDescription1', { brandName: brand.name })}</p>
                <p>{t('settings:mission.ahpDescription2')}</p>
              </>
            ) : (
              <>
                <p>{t('settings:mission.luigisDescription1', { brandName: brand.name })}</p>
                <p>{t('settings:mission.luigisDescription2')}</p>
              </>
            )}
            {showPayments && (
            <div className="pt-4 flex flex-col gap-3">
              <Link to="/donate">
                <Button
                  className={`w-full ${
                    isAntigreed
                      ? 'bg-emerald-700 hover:bg-emerald-800 text-white border-emerald-700'
                      : !isAhp
                      ? 'border-matrix-green text-matrix-green hover:bg-matrix-green/10'
                      : ''
                  }`}
                  variant="outline"
                  style={isAhp ? {
                    borderColor: forest,
                    color: forest,
                    backgroundColor: 'transparent',
                  } : undefined}
                  onMouseEnter={isAhp ? (e) => (e.currentTarget.style.backgroundColor = ahpDark ? 'rgba(90,173,128,0.12)' : 'rgba(43,94,73,0.1)') : undefined}
                  onMouseLeave={isAhp ? (e) => (e.currentTarget.style.backgroundColor = 'transparent') : undefined}
                >
                  <Heart className="w-4 h-4 mr-2" />
                  {t('common:common.supportOurMission')}
                </Button>
              </Link>
            </div>
            )}
          </CardContent>
        </Card>

        {/* Footer Info */}
        <div
          className={`text-center text-xs space-y-2 py-4 ${isAntigreed ? 'text-gray-600' : !isAhp ? 'text-muted-foreground' : ''}`}
          style={isAhp ? { color: subtle } : undefined}
        >
          <p className="font-mono">
            {t('settings:footer.madeWith')} <Heart
              className="w-3 h-3 inline"
              style={isAhp ? { color: ahpDark ? '#e85d6c' : '#B22234' } : undefined}
              {...(!isAhp ? { className: `w-3 h-3 inline ${isAntigreed ? 'text-red-600' : 'text-blood-red'}` } : { className: 'w-3 h-3 inline' })}
            /> {t('settings:footer.forHealthcareTransparency')}
          </p>
          <p>
            © {new Date().getFullYear()} {brand.legal.organization}. {t('settings:footer.allRightsReserved')}
          </p>
          <p className="font-semibold">
            {t('settings:footer.productOf')}
          </p>
          <p className="text-[10px]">
            {t('settings:footer.hospitalDataSourced')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default Settings;
